import React, { useState } from 'react';
import { GroupMemberProfile, CommunityRating } from '../types';
import { X, Star, Users, Check, Award } from 'lucide-react';
import { calculateCommunityAverageOVR } from '../services/ratingCalculator';

interface CommunityScoutingModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetPlayer: GroupMemberProfile;
  currentUserId: string;
  isOrganizer: boolean;
  onSubmitRating: (targetMemberId: string, rating: Partial<CommunityRating>) => void;
  onApplyCommunitySuggestion?: (targetMemberId: string, suggestedAttrs: any) => void;
}

export const CommunityScoutingModal: React.FC<CommunityScoutingModalProps> = ({
  isOpen,
  onClose,
  targetPlayer,
  currentUserId,
  isOrganizer,
  onSubmitRating,
  onApplyCommunitySuggestion,
}) => {
  const isGk = targetPlayer.primaryPosition === 'GK';
  const existingUserRating = targetPlayer.communityRatings?.find(
    (r) => r.authorMemberId === currentUserId
  );

  const [pace, setPace] = useState(existingUserRating?.pace || targetPlayer.footballCard.pace || 65);
  const [shooting, setShooting] = useState(existingUserRating?.shooting || targetPlayer.footballCard.shooting || 65);
  const [passing, setPassing] = useState(existingUserRating?.passing || targetPlayer.footballCard.passing || 65);
  const [dribbling, setDribbling] = useState(existingUserRating?.dribbling || targetPlayer.footballCard.dribbling || 65);
  const [defending, setDefending] = useState(existingUserRating?.defending || targetPlayer.footballCard.defending || 65);
  const [physical, setPhysical] = useState(existingUserRating?.physical || targetPlayer.footballCard.physical || 65);

  const [submitted, setSubmitted] = useState(false);

  const communityAttrs = calculateCommunityAverageOVR(targetPlayer.communityRatings || [], isGk);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmitRating(targetPlayer.id, {
      pace,
      shooting,
      passing,
      dribbling,
      defending,
      physical,
      timestamp: new Date().toISOString(),
    });
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 1500);
  };

  const handleApplySuggestion = () => {
    if (communityAttrs && onApplyCommunitySuggestion) {
      onApplyCommunitySuggestion(targetPlayer.id, communityAttrs);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#121614] border border-white/10 rounded-2xl w-full max-w-lg p-5 sm:p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <img
              src={targetPlayer.avatar}
              alt={targetPlayer.displayName}
              className="w-10 h-10 rounded-xl object-cover border border-white/10"
            />
            <div>
              <h3 className="font-bold text-base text-white">Scout Player: {targetPlayer.displayName}</h3>
              <p className="text-xs text-slate-400 font-medium">Anonymous peer scouting & attributes</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Official vs Community Overview */}
        <div className="grid grid-cols-2 gap-3 bg-[#171c19] p-3.5 rounded-xl border border-white/5 mb-5">
          <div className="text-center border-r border-white/5 pr-2">
            <span className="text-[11px] text-slate-400 font-medium uppercase block">Official OVR</span>
            <span className="text-2xl font-extrabold text-white font-mono">{targetPlayer.footballCard.overall}</span>
          </div>
          <div className="text-center pl-2">
            <span className="text-[11px] text-slate-400 font-medium uppercase block">Community OVR</span>
            <span className="text-2xl font-extrabold text-[#a8c936] font-mono">
              {communityAttrs ? communityAttrs.overall : 'N/A'}
            </span>
            <span className="text-[10px] text-slate-400 font-medium block">
              ({targetPlayer.communityRatings?.length || 0} votes)
            </span>
          </div>
        </div>

        {/* Organizer action to apply community rating */}
        {isOrganizer && communityAttrs && (
          <div className="mb-5 p-3 rounded-xl bg-[#1c221d] border border-[#a8c936]/20 flex items-center justify-between gap-3">
            <div>
              <span className="text-xs font-semibold text-white block">Community Suggestion Ready</span>
              <span className="text-[11px] text-slate-400 font-medium">
                Calculated from {targetPlayer.communityRatings?.length} peer ratings
              </span>
            </div>
            <button
              onClick={handleApplySuggestion}
              className="px-3 py-1.5 bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] font-bold text-xs rounded-xl transition shrink-0"
            >
              Apply Suggestion
            </button>
          </div>
        )}

        {/* Rating Sliders Form */}
        {submitted ? (
          <div className="py-8 text-center space-y-2">
            <div className="w-12 h-12 bg-[#a8c936]/20 border border-[#a8c936]/40 text-[#a8c936] rounded-full flex items-center justify-center mx-auto">
              <Check className="w-6 h-6" />
            </div>
            <h4 className="font-bold text-base text-white">Rating Submitted!</h4>
            <p className="text-xs text-slate-400 font-medium">Your anonymous scouting input has been recorded.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <span className="text-xs font-semibold text-slate-300 block">
              Rate {targetPlayer.displayName}'s attributes (1 - 99):
            </span>

            <div className="space-y-3">
              {[
                { label: 'Pace (PAC)', val: pace, set: setPace },
                { label: 'Shooting (SHO)', val: shooting, set: setShooting },
                { label: 'Passing (PAS)', val: passing, set: setPassing },
                { label: 'Dribbling (DRI)', val: dribbling, set: setDribbling },
                { label: 'Defending (DEF)', val: defending, set: setDefending },
                { label: 'Physical (PHY)', val: physical, set: setPhysical },
              ].map((item) => (
                <div key={item.label} className="bg-[#171c19] p-2.5 rounded-xl border border-white/5 space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-300 font-medium">{item.label}</span>
                    <span className="font-mono font-bold text-[#a8c936]">{item.val}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="99"
                    value={item.val}
                    onChange={(e) => item.set(parseInt(e.target.value))}
                    className="w-full accent-[#a8c936] bg-[#080b0a] rounded-lg h-1.5 cursor-pointer"
                  />
                </div>
              ))}
            </div>

            <div className="pt-2 flex items-center justify-end gap-3 border-t border-white/5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-xs font-semibold bg-[#a8c936] hover:bg-[#b8db3d] text-[#080b0a] rounded-xl transition"
              >
                Submit Rating
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
